import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../hooks/useAuth';
import { Clock, Check, Plus, Search, Repeat, Edit, X, Eye, ArrowRight } from 'lucide-react';
import { TradeCard } from '../../components/trades/TradeCard';
import { Toast } from '../../components/ui/Toast';
import { PageTitle } from '../../components/layout/PageTitle';
import { PageLayout } from '../../components/layout/PageLayout';
import { MyTradesSkeleton } from '../../components/skeletons/MyTradesSkeleton';
import { AdSense } from '../../components/ads/AdSense';
import { useTranslation } from '../../hooks/useTranslation';

interface UserCard {
  quantity: number;
  user_id: string;
}

interface Card {
  id: number;
  card_fr: string;
  image: string;
  set: string;
  set_info?: {
    display_name: string;
    display_name_en?: string;
    img: string | null;
  };
  rarity: string;
  user_cards?: UserCard[];
}

interface TradeCard {
  id: string;
  card: Card;
}

interface Trade {
  id: string;
  status: 'pending' | 'accepted' | 'rejected' | 'completed';
  created_at: string;
  user_id: string;
  accepted_by: string | null;
  accepted_card_id: string | null;
  wanted_card: Card;
  trade_cards: TradeCard[];
  creator_pokemon_id: string | null;
  acceptor_pokemon_id: string | null;
  creator?: {
    username: string;
    avatar_url?: string;
  };
  acceptor?: {
    username: string;
  };
}

interface TradeStats {
  total: number;
  pending: number;
  accepted: number;
  completed: number;
}

interface TradeState {
  created: Trade[];
  accepted: Trade[];
  history: Trade[];
  stats: TradeStats;
}

const createTradeStatus = (t: (key: string) => string) => ({
  all: { label: t('trades.status.all'), icon: Search, className: 'text-gray-600 bg-gray-50' },
  pending: { label: t('trades.status.pending'), icon: Clock, className: 'text-yellow-600 bg-yellow-50' },
  accepted: { label: t('trades.status.accepted'), icon: Check, className: 'text-green-600 bg-green-50' },
});

interface ConfirmModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  message: string;
}

const ConfirmModal = ({ isOpen, onClose, onConfirm, title, message }: ConfirmModalProps) => {
  const { t } = useTranslation();
  
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
        <h3 className="text-lg font-medium text-gray-900 mb-2">{title}</h3>
        <p className="text-gray-500 mb-6">{message}</p>
        <div className="flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-800"
          >
            {t('common.cancel')}
          </button>
          <button
            onClick={onConfirm}
            className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700"
          >
            {t('trades.actions.delete')}
          </button>
        </div>
      </div>
    </div>
  );
};

// Remplacer la fonction formatRelativeTime pour supporter le français et l'anglais
const formatRelativeTime = (date: Date, lang: string) => {
  const now = new Date();
  const diffInMinutes = Math.floor((now.getTime() - date.getTime()) / (1000 * 60));
  const diffInHours = Math.floor(diffInMinutes / 60);
  const diffInDays = Math.floor(diffInHours / 24);
  const diffInMonths = Math.floor(diffInDays / 30);

  const translations = {
    fr: {
      month: 'mois',
      months: 'mois',
      day: 'jour',
      days: 'jours',
      hour: 'heure',
      hours: 'heures',
      minute: 'minute',
      minutes: 'minutes',
      justNow: 'à l\'instant'
    },
    en: {
      month: 'month',
      months: 'months',
      day: 'day',
      days: 'days',
      hour: 'hour',
      hours: 'hours',
      minute: 'minute',
      minutes: 'minutes',
      justNow: 'just now'
    }
  };

  const t = translations[lang === 'en' ? 'en' : 'fr'];

  if (diffInMonths > 0) {
    return `${diffInMonths} ${diffInMonths === 1 ? t.month : t.months}`;
  } else if (diffInDays > 0) {
    return `${diffInDays} ${diffInDays === 1 ? t.day : t.days}`;
  } else if (diffInHours > 0) {
    return `${diffInHours} ${diffInHours === 1 ? t.hour : t.hours}`;
  } else if (diffInMinutes > 0) {
    return `${diffInMinutes} ${diffInMinutes === 1 ? t.minute : t.minutes}`;
  }
  return t.justNow;
};

export const MyTrades = () => {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const { t, lang, getPathForLang } = useTranslation();
  const [statusFilter, setStatusFilter] = useState<keyof ReturnType<typeof createTradeStatus>>('all');
  const TradeStatus = createTradeStatus(t);

  const [trades, setTrades] = useState<TradeState>({ 
    created: [], 
    accepted: [], 
    history: [],
    stats: {
      total: 0,
      pending: 0,
      accepted: 0,
      completed: 0
    }
  });
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'created' | 'accepted' | 'history'>('created');
  const [toast, setToast] = useState<{
    message: string;
    type: 'success' | 'error';
  } | null>(null);
  const [confirmModal, setConfirmModal] = useState({
    isOpen: false,
    tradeId: null as string | null,
  });

  // Charger les statistiques globales séparément
  const loadStats = async () => {
    if (!user) return;
    
    try {
      // Requête pour les trades en attente (mes offres en pending)
      const { data: pendingCount, error: pendingError } = await supabase
        .from('trades')
        .select('id', { count: 'exact' })
        .eq('user_id', user.id)
        .eq('status', 'pending');

      // Requête pour les trades acceptés (mes offres acceptées + trades que j'ai acceptés)
      const { data: acceptedCount, error: acceptedError } = await supabase
        .from('trades')
        .select('id', { count: 'exact' })
        .eq('status', 'accepted')
        .or(`user_id.eq.${user.id},accepted_by.eq.${user.id}`);

      // Requête pour les trades complétés
      const { data: completedCount, error: completedError } = await supabase
        .from('trades')
        .select('id', { count: 'exact' })
        .eq('status', 'completed')
        .or(`user_id.eq.${user.id},accepted_by.eq.${user.id}`);

      if (pendingError || acceptedError || completedError) throw pendingError || acceptedError || completedError;

      // Calculer le total
      const total = (pendingCount?.length || 0) + (acceptedCount?.length || 0) + (completedCount?.length || 0);

      // Mettre à jour les statistiques
      setTrades(prev => ({
        ...prev,
        stats: {
          total,
          pending: pendingCount?.length || 0,
          accepted: acceptedCount?.length || 0,
          completed: completedCount?.length || 0
        }
      }));
    } catch (error) {
      console.error('Error loading stats:', error);
    }
  };

  // Fonction pour obtenir le nom d'affichage du set
  const getSetDisplayName = (card: {
    set: string;
    set_info?: {
      display_name: string;
      display_name_en?: string;
    };
  }) => {
    if (card.set_info) {
      return lang === 'en' && card.set_info.display_name_en 
        ? card.set_info.display_name_en 
        : card.set_info.display_name || card.set;
    }
    return card.set;
  };

  // Charger les trades en fonction de l'onglet actif
  const loadTradesByTab = async (tab: 'created' | 'accepted' | 'history') => {
    if (!user) return;
    
    setLoading(true);
    try {
      let query = supabase
        .from('trades')
        .select(`
          id,
          status,
          created_at,
          user_id,
          accepted_by,
          accepted_card_id,
          wanted_card:cards!trades_wanted_card_id_fkey(
            id,
            card_fr,
            image,
            set,
            rarity
          ),
          trade_cards!trade_cards_trade_id_fkey(
            id,
            card:cards!trade_cards_card_id_fkey(
              id,
              card_fr,
              image,
              set,
              rarity
            )
          ),
          creator:profiles!trades_user_id_fkey1(
            username,
            avatar_url
          ),
          acceptor:profiles!trades_accepted_by_fkey1(
            username
          )
        `);

      // Appliquer les filtres en fonction de l'onglet
      switch (tab) {
        case 'created':
          query = query
            .eq('user_id', user.id)
            .neq('status', 'completed')
            .order('created_at', { ascending: false });
          break;
        case 'accepted':
          query = query
            .eq('accepted_by', user.id)
            .eq('status', 'accepted')
            .order('created_at', { ascending: false });
          break;
        case 'history':
          query = query
            .eq('status', 'completed')
            .or(`user_id.eq.${user.id},accepted_by.eq.${user.id}`)
            .order('created_at', { ascending: false });
          break;
      }

      const { data, error } = await query;

      if (error) throw error;

      // Récupérer tous les noms de sets uniques pour charger leurs informations
      if (data && data.length > 0) {
        const setNames = new Set<string>();
        
        // Collecter tous les noms de sets
        data.forEach(trade => {
          setNames.add(trade.wanted_card.set);
          trade.trade_cards.forEach(tc => {
            setNames.add(tc.card.set);
          });
        });

        // Charger les informations des sets
        await loadSetInfo(data, Array.from(setNames));
      }

      setTrades(prev => ({
        ...prev,
        [tab]: data || []
      }));
    } catch (error) {
      console.error(`Error loading ${tab} trades:`, error);
    } finally {
      setLoading(false);
    }
  };

  // Fonction pour charger les informations des sets
  const loadSetInfo = async (trades: any[], setNames: string[]) => {
    if (setNames.length === 0) return;

    try {
      const { data: setsData, error } = await supabase
        .from('sets')
        .select('name, display_name, display_name_en, img')
        .in('name', setNames);

      if (error) throw error;

      if (setsData && setsData.length > 0) {
        // Créer un map des informations de sets
        const setsMap = new Map();
        setsData.forEach(set => {
          setsMap.set(set.name, {
            display_name: set.display_name || set.name,
            display_name_en: set.display_name_en,
            img: set.img
          });
        });

        // Mettre à jour les informations de sets pour chaque trade
        trades.forEach(trade => {
          if (setsMap.has(trade.wanted_card.set)) {
            trade.wanted_card.set_info = setsMap.get(trade.wanted_card.set);
          }
          
          trade.trade_cards.forEach(tc => {
            if (setsMap.has(tc.card.set)) {
              tc.card.set_info = setsMap.get(tc.card.set);
            }
          });
        });
      }
    } catch (err) {
      console.error('Error loading set info:', err);
    }
  };

  useEffect(() => {
    if (authLoading || !user) return;
    
    // Charger les statistiques au montage
    loadStats();
    // Charger les trades de tous les onglets
    Promise.all([
      loadTradesByTab('created'),
      loadTradesByTab('accepted'),
      loadTradesByTab('history')
    ]);
  }, [user, authLoading]);

  const handleCancelTrade = async (tradeId: string) => {
    setConfirmModal({
      isOpen: true,
      tradeId,
    });
  };

  const confirmCancelTrade = async () => {
    if (!confirmModal.tradeId) return;

    try {
      const { error } = await supabase
        .from('trades')
        .delete()
        .eq('id', confirmModal.tradeId)
        .eq('user_id', user?.id)
        .eq('status', 'pending');

      if (error) throw error;

      // Mettre à jour l'état local en filtrant le trade supprimé
      if (activeTab === 'created') {
        setTrades(prev => ({
          ...prev,
          created: prev.created.filter(trade => trade.id !== confirmModal.tradeId)
        }));
      } else if (activeTab === 'accepted') {
        setTrades(prev => ({
          ...prev,
          accepted: prev.accepted.filter(trade => trade.id !== confirmModal.tradeId)
        }));
      }

      // Afficher un message de succès
      setToast({
        message: "L'échange a été supprimé avec succès",
        type: 'success'
      });
    } catch (error) {
      console.error('Error deleting trade:', error);
      setToast({
        message: "Une erreur est survenue lors de la suppression de l'échange",
        type: 'error'
      });
    } finally {
      setConfirmModal({ isOpen: false, tradeId: null });
    }
  };

  const handleEditTrade = (tradeId: string) => {
    navigate(getPathForLang(`/trades/${tradeId}/edit`, lang));
  };

  const filteredTrades = activeTab === 'created' 
    ? trades[activeTab].filter(trade => statusFilter === 'all' ? true : trade.status === statusFilter)
    : trades[activeTab];

  console.log('MyTrades rendering, loading:', loading, 'trades count:', trades.created.length + trades.accepted.length);

  if (authLoading || loading) {
    return (
      <PageLayout
        breadcrumbProps={{
          items: [
            { label: t('trades.breadcrumb.trades'), path: getPathForLang('/trades/browse', lang), icon: <Repeat className="w-4 h-4" /> },
            { label: t('common.loading'), path: '' }
          ]
        }}
      >
        <MyTradesSkeleton />
      </PageLayout>
    );
  }

  return (
    <PageLayout
      breadcrumbProps={{
        items: [
          { label: t('trades.breadcrumb.trades'), path: getPathForLang('/trades/browse', lang) },
          { label: t('trades.breadcrumb.my_trades'), path: getPathForLang('/trades/my-trades', lang) }
        ]
      }}
    >
      <div className="max-w-7xl mx-auto px-0 sm:px-4 py-4 sm:py-8">
        <PageTitle title={t('trades.my_trades.title')} />
        
        {/* En-tête avec statistiques */}
        <div className="mb-6 px-4 sm:px-0">
          <div className="flex justify-between items-start mb-6">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">{t('trades.my_trades.title')}</h1>
              <p className="text-gray-500 mt-1">
                {t('trades.my_trades.subtitle')}
              </p>
            </div>
            <Link
              to={getPathForLang('/trades/create', lang)}
              className="inline-flex items-center px-4 py-2 sm:px-5 sm:py-2.5 bg-blue-600 text-white text-sm sm:text-base rounded-lg hover:bg-blue-700 transition-colors shadow-sm whitespace-nowrap"
            >
              <Plus className="w-4 h-4 sm:w-5 sm:h-5 mr-1.5 sm:mr-2" />
              <span className="hidden sm:inline">{t('trades.actions.new_trade')}</span>
              <span className="sm:hidden">{t('trades.actions.new_short')}</span>
            </Link>
          </div>

          {/* Cartes de statistiques */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4">
            {[
              { 
                label: t('trades.stats.total'), 
                count: trades.stats.total,
                color: 'bg-blue-50 text-blue-700' 
              },
              { 
                label: t('trades.stats.pending'), 
                count: trades.stats.pending,
                color: 'bg-yellow-50 text-yellow-700' 
              },
              { 
                label: t('trades.stats.accepted'), 
                count: trades.stats.accepted,
                color: 'bg-green-50 text-green-700' 
              },
              { 
                label: t('trades.stats.completed'), 
                count: trades.stats.completed,
                color: 'bg-purple-50 text-purple-700' 
              },
            ].map((stat) => (
              <div key={stat.label} className={`${stat.color} rounded-lg p-3 sm:p-4`}>
                <p className="text-sm font-medium">{stat.label}</p>
                <p className="text-xl sm:text-2xl font-bold mt-1">{stat.count}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Première publicité (en haut) */}
        <div className="bg-white rounded-xl overflow-hidden shadow-sm border border-gray-100 mb-6 max-w-[728px] mx-auto flex justify-center items-center">
          <AdSense
            adSlot="1424794127"
            className="w-full min-h-[90px]"
          />
        </div>

        {/* Navigation par onglets et filtres */}
        <div className="bg-white shadow mb-6 rounded-lg">
          <div className="border-b border-gray-200">
            <div className="flex flex-col sm:flex-row justify-between items-stretch sm:items-center px-4 sm:px-6">
              <nav className="flex w-full" aria-label={t('trades.tabs.aria_label')}>
                <button
                  onClick={() => setActiveTab('created')}
                  className={`
                    flex-1 py-3 sm:py-4 px-3 sm:px-6 border-b-2 font-medium text-xs sm:text-sm flex items-center justify-center
                    ${activeTab === 'created'
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }
                  `}
                >
                  <div className="flex flex-col items-center sm:flex-row sm:items-center">
                    <Plus className="w-5 h-5 mb-1 sm:mb-0 sm:mr-2" />
                    <span>{t('trades.tabs.my_offers')}</span>
                    <span className="ml-1">({trades.created.filter(t => t.status !== 'completed').length})</span>
                  </div>
                </button>
                <button
                  onClick={() => setActiveTab('accepted')}
                  className={`
                    flex-1 py-3 sm:py-4 px-3 sm:px-6 border-b-2 font-medium text-xs sm:text-sm flex items-center justify-center
                    ${activeTab === 'accepted'
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }
                  `}
                >
                  <div className="flex flex-col items-center sm:flex-row sm:items-center">
                    <Check className="w-5 h-5 mb-1 sm:mb-0 sm:mr-2" />
                    <span>{t('trades.tabs.other_offers')}</span>
                    <span className="ml-1">({trades.accepted.filter(t => t.status === 'accepted').length})</span>
                  </div>
                </button>
                <button
                  onClick={() => setActiveTab('history')}
                  className={`
                    flex-1 py-3 sm:py-4 px-3 sm:px-6 border-b-2 font-medium text-xs sm:text-sm flex items-center justify-center
                    ${activeTab === 'history'
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }
                  `}
                >
                  <div className="flex flex-col items-center sm:flex-row sm:items-center">
                    <Clock className="w-5 h-5 mb-1 sm:mb-0 sm:mr-2" />
                    <span>{t('trades.tabs.history')}</span>
                    <span className="ml-1">({trades.history.length})</span>
                  </div>
                </button>
              </nav>

              {/* Filtre par statut */}
              {activeTab === 'created' && (
                <div className="py-2 sm:py-4 border-t sm:border-t-0 border-gray-200 w-full sm:w-auto">
                  <select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value as keyof ReturnType<typeof createTradeStatus>)}
                    className="w-full sm:w-auto px-3 py-1.5 sm:py-2 text-sm border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                  >
                    {Object.entries(TradeStatus).map(([key, { label }]) => (
                      <option key={key} value={key}>
                        {label}
                      </option>
                    ))}
                  </select>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Liste des échanges */}
        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 animate-fadeIn">
            {[1, 2, 3].map((i) => (
              <div key={i} className="animate-pulse bg-white rounded-lg shadow-sm border border-gray-200 p-4">
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0 w-28">
                    <div className="h-8 bg-gray-200 rounded-full mb-4"></div>
                    <div className="aspect-[3/4] bg-gray-200 rounded-lg mb-3"></div>
                    <div className="space-y-2">
                      <div className="h-4 bg-gray-200 rounded w-full"></div>
                      <div className="h-3 bg-gray-200 rounded w-2/3 mx-auto"></div>
                    </div>
                  </div>
                  <div className="flex-1">
                    <div className="flex justify-end mb-2">
                      <div className="h-4 w-24 bg-gray-200 rounded-full"></div>
                    </div>
                    <div className="space-y-2">
                      <div className="h-4 w-32 bg-gray-200 rounded"></div>
                      <div className="grid grid-cols-3 gap-2">
                        {[...Array(6)].map((_, index) => (
                          <div key={index} className="aspect-[3/4] bg-gray-200 rounded-lg"></div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : filteredTrades.length > 0 ? (
          <section 
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 animate-fadeIn"
            aria-label={t('trades.list.aria_label')}
          >
            {filteredTrades.map((trade) => (
              <div 
                key={trade.id} 
                className="bg-white rounded-lg shadow-sm border border-gray-200 hover:shadow-md transition-shadow"
              >
                <div className="p-4">
                  {/* En-tête avec statut */}
                  <div className="flex justify-between items-center mb-4">
                    <div className="flex items-center space-x-2">
                      {trade.creator?.username && (
                        <>
                          {trade.creator.avatar_url ? (
                            <img
                              src={trade.creator.avatar_url}
                              alt={t('trades.avatar_alt', { username: trade.creator.username })}
                              className="w-8 h-8 rounded-full border border-gray-200"
                            />
                          ) : (
                            <div className="w-8 h-8 rounded-full bg-primary-100 flex items-center justify-center">
                              <span className="text-primary-600 font-semibold">
                                {trade.creator.username.charAt(0).toUpperCase()}
                              </span>
                            </div>
                          )}
                          <span className="text-sm font-medium text-gray-900 truncate max-w-[100px]">
                            {activeTab === 'created' ? t('trades.your_offer') : trade.creator.username}
                          </span>
                        </>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <span className={`text-xs px-2 py-1 rounded-full ${
                        trade.status === 'pending' ? 'text-yellow-600 bg-yellow-50' :
                        trade.status === 'accepted' ? 'text-green-600 bg-green-50' :
                        'text-blue-600 bg-blue-50'
                      }`}>
                        {trade.status === 'pending' ? t('trades.status.pending') :
                         trade.status === 'accepted' ? t('trades.status.accepted') :
                         t('trades.status.completed')}
                      </span>
                      <span className="text-xs text-gray-500 bg-gray-50 px-2 py-1 rounded-full">
                        {formatRelativeTime(new Date(trade.created_at), lang)}
                      </span>
                    </div>
                  </div>

                  {/* Contenu principal */}
                  <div className="flex flex-col">
                    <div className="flex items-start gap-4 mb-4">
                      {/* Carte voulue - affichée uniquement pour les trades en attente */}
                      {trade.status === 'pending' && (
                        <div className="flex-shrink-0 w-28">
                          <div className="aspect-[3/4]">
                            <img
                              src={`https://image.pokemonpocket.fr/preview/${trade.wanted_card.image}`}
                              alt={trade.wanted_card.card_fr}
                              className="w-full h-full object-contain rounded-lg"
                              loading="lazy"
                            />
                          </div>
                          <div className="text-center mt-2">
                            <h3 className="text-sm font-medium text-gray-900 line-clamp-1">{trade.wanted_card.card_fr}</h3>
                            <p className="text-xs text-gray-500 line-clamp-1">{getSetDisplayName(trade.wanted_card)}</p>
                          </div>
                        </div>
                      )}

                      {/* Cartes proposées ou échange */}
                      <div className="flex-1">
                        <h4 className="text-xs font-medium text-gray-500 mb-2">
                          {trade.status === 'pending' ? t('trades.offered_cards') : ''}
                        </h4>
                        {trade.status === 'pending' ? (
                          <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                            {trade.trade_cards.slice(0, trade.trade_cards.length > 6 ? 5 : 6).map((tc) => (
                              <div key={tc.id} className="relative group">
                                <img
                                  src={`https://image.pokemonpocket.fr/preview/${tc.card.image}`}
                                  alt={tc.card.card_fr}
                                  className="w-full aspect-[3/4] object-contain rounded-lg"
                                  loading="lazy"
                                />
                                <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-50 transition-opacity duration-200 rounded-lg flex items-center justify-center">
                                  <span className="text-white text-xs font-medium opacity-0 group-hover:opacity-100 transition-opacity duration-200 text-center px-1">
                                    {tc.card.card_fr}
                                  </span>
                                </div>
                              </div>
                            ))}
                            {trade.trade_cards.length > 6 && (
                              <div className="relative flex items-center justify-center bg-gray-100 aspect-[3/4] rounded-lg">
                                <span className="text-gray-700 text-sm">
                                  +{trade.trade_cards.length - 5}
                                </span>
                              </div>
                            )}
                          </div>
                        ) : (
                          <div className="flex items-center justify-center gap-8 -mx-4">
                            <div className="w-28">
                              <div className="aspect-[3/4]">
                                <img
                                  src={`https://image.pokemonpocket.fr/preview/${trade.wanted_card.image}`}
                                  alt={trade.wanted_card.card_fr}
                                  className="w-full h-full object-contain rounded-lg"
                                  loading="lazy"
                                />
                              </div>
                              <div className="text-center mt-2">
                                <h3 className="text-sm font-medium text-gray-900 line-clamp-1">{trade.wanted_card.card_fr}</h3>
                                <p className="text-xs text-gray-500 line-clamp-1">{getSetDisplayName(trade.wanted_card)}</p>
                              </div>
                            </div>

                            <div className="flex flex-col items-center justify-center">
                              <ArrowRight className="w-8 h-8 text-blue-500" />
                              <span className="text-xs text-gray-500 mt-1">{t('trades.exchange')}</span>
                            </div>

                            <div className="w-28">
                              {trade.accepted_card_id && (
                                <>
                                  <div className="aspect-[3/4]">
                                    <img
                                      src={`https://image.pokemonpocket.fr/preview/${trade.trade_cards.find(tc => tc.id === trade.accepted_card_id)?.card.image}`}
                                      alt="Carte choisie"
                                      className="w-full h-full object-contain rounded-lg"
                                      loading="lazy"
                                    />
                                  </div>
                                  <div className="text-center mt-2">
                                    <p className="text-sm font-medium text-gray-900 line-clamp-1">
                                      {trade.trade_cards.find(tc => tc.id === trade.accepted_card_id)?.card.card_fr}
                                    </p>
                                    <p className="text-xs text-gray-500 line-clamp-1">
                                      {trade.trade_cards.find(tc => tc.id === trade.accepted_card_id)?.card.set_info ? 
                                        getSetDisplayName(trade.trade_cards.find(tc => tc.id === trade.accepted_card_id)?.card as Card) :
                                        trade.trade_cards.find(tc => tc.id === trade.accepted_card_id)?.card.set}
                                    </p>
                                  </div>
                                </>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Boutons d'action */}
                    <div className="mt-4 pt-3 border-t border-gray-100">
                      {activeTab !== 'history' && trade.status === 'pending' ? (
                        <div className="flex gap-2">
                          <button
                            onClick={() => handleEditTrade(trade.id)}
                            className="flex-1 inline-flex items-center justify-center px-3 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors"
                          >
                            <Edit className="w-4 h-4 mr-2" />
                            {t('trades.actions.edit')}
                          </button>
                          <button
                            onClick={() => handleCancelTrade(trade.id)}
                            className="flex-1 inline-flex items-center justify-center px-3 py-2 bg-red-600 text-white text-sm font-medium rounded-lg hover:bg-red-700 transition-colors"
                          >
                            <X className="w-4 h-4 mr-2" />
                            {t('trades.actions.cancel')}
                          </button>
                        </div>
                      ) : (
                        <Link
                          to={getPathForLang(`/trades/${trade.id}`, lang)}
                          className="w-full inline-flex items-center justify-center px-3 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors"
                        >
                          <Eye className="w-4 h-4 mr-2" />
                          {t('trades.actions.view_details')}
                        </Link>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </section>
        ) : (
          <div className="bg-white rounded-lg shadow p-12 text-center">
            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              {activeTab === 'created' ? (
                <Plus className="w-8 h-8 text-gray-400" />
              ) : activeTab === 'accepted' ? (
                <Check className="w-8 h-8 text-gray-400" />
              ) : (
                <Clock className="w-8 h-8 text-gray-400" />
              )}
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              {activeTab === 'created' 
                ? t('trades.empty.created.title')
                : activeTab === 'accepted'
                ? t('trades.empty.accepted.title')
                : t('trades.empty.history.title')
              }
            </h3>
            <p className="text-gray-500 mb-6">
              {activeTab === 'created' 
                ? t('trades.empty.created.description')
                : activeTab === 'accepted'
                ? t('trades.empty.accepted.description')
                : t('trades.empty.history.description')
              }
            </p>
            {activeTab === 'created' ? (
              <Link
                to={getPathForLang('/trades/create', lang)}
                className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
              >
                <Plus className="w-5 h-5 mr-2" />
                {t('trades.actions.propose_trade')}
              </Link>
            ) : activeTab === 'accepted' ? (
              <Link
                to={getPathForLang('/trades/browse', lang)}
                className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
              >
                <Search className="w-5 h-5 mr-2" />
                {t('trades.actions.browse_trades')}
              </Link>
            ) : null}
          </div>
        )}

        {/* Publicité en bas de page */}
        <div className="mt-6 mb-8 w-full flex justify-center items-center overflow-hidden">
          <div className="flex justify-center items-center">
            <AdSense adSlot="1424794127" id="page-bottom" />
          </div>
        </div>

        {/* Modals and toasts */}
        <ConfirmModal
          isOpen={confirmModal.isOpen}
          onClose={() => setConfirmModal({ isOpen: false, tradeId: null })}
          onConfirm={confirmCancelTrade}
          title={t('trades.confirm_modal.title')}
          message={t('trades.confirm_modal.message')}
        />

        {toast && (
          <Toast
            message={toast.message}
            type={toast.type}
            onClose={() => setToast(null)}
          />
        )}
      </div>
    </PageLayout>
  );
}; 